// Pending Checkout Tracker
//
// Why this exists: most users finish the Stripe → return page round-trip in
// under 5 seconds and see the in-page success card. But some close the tab,
// follow a deep link from another window, or watch the polling page time out
// before the webhook lands. Without this hook, those users get no
// acknowledgment that their purchase succeeded.
//
// How it works:
//   1. Every time we initiate a Stripe Checkout Session (slot top-up, AI
//      credits, subscription), we call `recordPendingCheckout()` to drop a
//      `{ session_id, kind, ts }` record into localStorage.
//   2. The return pages call `clearPendingCheckout()` once they reach a
//      terminal phase (success or failed) — this prevents double-toasts.
//   3. The dashboard mounts `usePendingCheckoutToast()`, which checks the
//      record once. If the entry is older than ~10s (i.e. user almost
//      certainly already saw or skipped the in-page confirmation), it polls
//      `/api/billing/checkout/status/{id}` once, fires the appropriate
//      sonner toast, and clears the record.
//
// We intentionally never toast on the return page itself — that path already
// renders a confirmation card.
import { useEffect } from 'react';
import { toast } from 'sonner';

const KEY = 'fedbd_pending_stripe_checkout';
const API_URL = process.env.REACT_APP_BACKEND_URL;

// Only poll once we're confident the user already left or skipped the
// return page (i.e. the in-page success card had time to render and clear
// itself). Anything younger than this, we assume the return page is still
// driving the UX and we leave it alone.
const MIN_AGE_MS = 10_000;
// Drop stale records — if a user opened a checkout 30 minutes ago and never
// finished, we've waited long enough; the webhook (the source of truth) has
// already applied or rejected the credit.
const MAX_AGE_MS = 30 * 60 * 1000;

const KIND_LABELS = {
  SLOT_TOPUP: { success: 'Pursuit slots added', noun: 'slots' },
  SUBSCRIPTION: { success: 'Subscription active', noun: 'plan' },
};

export function recordPendingCheckout(kind, sessionId) {
  if (!sessionId) return;
  try {
    localStorage.setItem(
      KEY,
      JSON.stringify({ kind, session_id: sessionId, ts: Date.now() })
    );
  } catch {
    // localStorage may be disabled — silently skip; loss of cross-page toast
    // is graceful (the in-page confirmation still works).
  }
}

export function clearPendingCheckout() {
  try { localStorage.removeItem(KEY); } catch { /* noop */ }
}

function readPendingCheckout() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || !obj.session_id) return null;
    return obj;
  } catch {
    return null;
  }
}

/**
 * Mount on any authenticated dashboard surface. Fires at most once per page
 * load and clears the record once a terminal status is observed.
 */
export function usePendingCheckoutToast(authToken) {
  useEffect(() => {
    if (!authToken) return;
    const pending = readPendingCheckout();
    if (!pending) return;

    const age = Date.now() - (pending.ts || 0);
    if (age < MIN_AGE_MS) return;        // return page is still driving
    if (age > MAX_AGE_MS) {                // stale — drop and skip
      clearPendingCheckout();
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const res = await fetch(
          `${API_URL}/api/billing/checkout/status/${pending.session_id}`,
          { headers: { Authorization: `Bearer ${authToken}` } },
        );
        if (cancelled) return;
        if (!res.ok) {
          // 404 / 403 — no record we can speak to. Drop it silently.
          clearPendingCheckout();
          return;
        }
        const data = await res.json();
        if (data.status === 'COMPLETED') {
          const labels = KIND_LABELS[pending.kind] || { success: 'Payment complete', noun: 'purchase' };
          toast.success(labels.success, {
            description: data.amount
              ? `Charged $${Number(data.amount).toFixed(2)} ${(data.currency || 'usd').toUpperCase()}.`
              : `Your ${labels.noun} are now active.`,
            duration: 6000,
          });
          clearPendingCheckout();
        } else if (data.status === 'FAILED' || data.status === 'EXPIRED') {
          toast.error('Payment did not complete', {
            description: 'No charges were made. Try again from Settings.',
            duration: 6000,
          });
          clearPendingCheckout();
        }
        // Otherwise still INITIATED — leave the record so the next dashboard
        // load can try again.
      } catch {
        // Network blip — leave the record alone for the next attempt.
      }
    })();

    return () => { cancelled = true; };
    // We intentionally only run this once per mount — the dashboard route
    // remount on navigation is enough to re-check.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);
}
